import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { VisualizerCore } from '@/components/visualizers/VisualizerCore';

/**
 * Prefer layout-box size (offsetWidth/Height) over getBoundingClientRect.
 * Rect includes CSS transforms (e.g. framer-motion scale entrance), which
 * permanently undersizes the canvas on first paint because ResizeObserver
 * does not fire when only a transform changes.
 */
function readLayoutSize(el) {
  if (!el) return { width: 0, height: 0 };
  return {
    width: Math.floor(el.offsetWidth || 0),
    height: Math.floor(el.offsetHeight || 0),
  };
}

const Visualizer = React.memo(function Visualizer({
  analyser,
  dataArray,
  isPlaying,
  visualizerType,
  onVisualizerChange,
  currentSong,
  currentTime,
  isStreamerMode,
  lyricsPosition,
  // Authoritative size from layout (avoids transform-skewed measurement)
  width: layoutWidth,
  height: layoutHeight,
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState(() => {
    const w = Math.floor(layoutWidth || 0);
    const h = Math.floor(layoutHeight || 0);
    return { width: w, height: h };
  });

  const analyserRef = useRef(analyser);
  const dataArrayRef = useRef(dataArray);

  useEffect(() => {
    analyserRef.current = analyser;
  }, [analyser]);

  useEffect(() => {
    dataArrayRef.current = dataArray;
  }, [dataArray]);

  // When layout already knows the stage size, trust it immediately
  useEffect(() => {
    const w = Math.floor(layoutWidth || 0);
    const h = Math.floor(layoutHeight || 0);
    if (w > 0 && h > 0) {
      setDimensions((prev) =>
        prev.width === w && prev.height === h ? prev : { width: w, height: h }
      );
    }
  }, [layoutWidth, layoutHeight]);

  const updateDimensions = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    // Prefer parent layout props when present; otherwise measure layout box
    const fromLayout = {
      width: Math.floor(layoutWidth || 0),
      height: Math.floor(layoutHeight || 0),
    };
    const measured = readLayoutSize(el);
    const next = {
      width: fromLayout.width > 0 ? fromLayout.width : measured.width,
      height: fromLayout.height > 0 ? fromLayout.height : measured.height,
    };

    if (next.width <= 0 || next.height <= 0) return;

    setDimensions((prev) =>
      prev.width === next.width && prev.height === next.height ? prev : next
    );
  }, [layoutWidth, layoutHeight]);

  useEffect(() => {
    updateDimensions();
    const rafId = requestAnimationFrame(updateDimensions);
    // One short follow-up after layout settles (no long delay)
    const t = setTimeout(updateDimensions, 50);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateDimensions);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleWindowResize = () => {
      requestAnimationFrame(updateDimensions);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [updateDimensions]);

  const visualizerProps = useMemo(
    () => ({
      analyserRef,
      dataArrayRef,
      isPlaying,
      visualizerType,
      onVisualizerChange,
      width: dimensions.width,
      height: dimensions.height,
      currentSong,
      currentTime,
      isStreamerMode,
      lyricsPosition,
    }),
    [
      isPlaying,
      visualizerType,
      onVisualizerChange,
      dimensions.width,
      dimensions.height,
      currentSong,
      currentTime,
      isStreamerMode,
      lyricsPosition,
    ]
  );

  const isReady = dimensions.width > 0 && dimensions.height > 0;

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative"
      style={{
        width: '100%',
        height: '100%',
        zIndex: 1,
      }}
    >
      {isReady ? (
        <VisualizerCore {...visualizerProps} />
      ) : (
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
            isStreamerMode
              ? 'bg-black'
              : 'bg-gradient-to-br from-slate-900/50 to-blue-900/50'
          }`}
        >
          <div className="text-center text-white">
            <div className="animate-pulse">
              <div className="w-12 h-12 mx-auto mb-4 bg-white/20 rounded-full" />
              <p className="text-lg font-medium">Initializing Visualizer...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export { Visualizer };
